import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ease } from "@/lib/motion";
import { ArrowUpRight, Target, Zap, Users, TrendingUp, BarChart3 } from "lucide-react";

interface WheelSegment {
  id: string;
  label: string;
  color: string;
  headline: string;
  description: string;
  ctaText: string;
  icon: React.ElementType;
  link: string;
  stats: { value: string; label: string };
}

const segments: WheelSegment[] = [
  {
    id: "acquire",
    label: "Acquire",
    color: "hsl(216, 85%, 55%)",
    headline: "Attract qualified demand",
    description: "Strategic acquisition that brings the right customers, not just traffic.",
    ctaText: "Improve demand quality",
    icon: Target,
    link: "/paid-marketing",
    stats: { value: "3.2x", label: "avg. ROAS lift" },
  },
  {
    id: "convert",
    label: "Convert",
    color: "hsl(169, 55%, 45%)",
    headline: "Turn interest into action",
    description: "Conversion systems built on insight, not guesswork.",
    ctaText: "Increase conversion rates",
    icon: Zap,
    link: "/cro",
    stats: { value: "+47%", label: "avg. CVR" },
  },
  {
    id: "retain",
    label: "Retain",
    color: "hsl(152, 50%, 42%)",
    headline: "Keep customers growing",
    description: "Retention strategies that compound customer lifetime value.",
    ctaText: "Reduce churn intelligently",
    icon: Users,
    link: "/email-marketing",
    stats: { value: "-32%", label: "avg. churn" },
  },
  {
    id: "scale",
    label: "Scale",
    color: "hsl(275, 50%, 52%)",
    headline: "Grow without breaking",
    description: "Infrastructure that scales predictably with your ambition.",
    ctaText: "Build predictable growth",
    icon: TrendingUp,
    link: "/solutions",
    stats: { value: "2.8x", label: "efficiency" },
  },
  {
    id: "intelligence",
    label: "Intelligence",
    color: "hsl(220, 25%, 45%)",
    headline: "Decide with clarity",
    description: "Analytics and insights that inform every growth decision.",
    ctaText: "Enable smarter decisions",
    icon: BarChart3,
    link: "/analytics",
    stats: { value: "100%", label: "visibility" },
  },
];

// Arc path generator
const createArcPath = (
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  const x1 = cx + innerRadius * Math.cos(startRad);
  const y1 = cy + innerRadius * Math.sin(startRad);
  const x2 = cx + outerRadius * Math.cos(startRad);
  const y2 = cy + outerRadius * Math.sin(startRad);
  const x3 = cx + outerRadius * Math.cos(endRad);
  const y3 = cy + outerRadius * Math.sin(endRad);
  const x4 = cx + innerRadius * Math.cos(endRad);
  const y4 = cy + innerRadius * Math.sin(endRad);

  return `
    M ${x1} ${y1}
    L ${x2} ${y2}
    A ${outerRadius} ${outerRadius} 0 0 1 ${x3} ${y3}
    L ${x4} ${y4}
    A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1}
    Z
  `;
};

interface HeroWheelProps {
  onSegmentHover?: (segment: WheelSegment | null) => void;
}

const HeroWheel = ({ onSegmentHover }: HeroWheelProps) => {
  const [activeSegment, setActiveSegment] = useState<string | null>(null);

  const activeData = segments.find((s) => s.id === activeSegment);

  const handleSegmentHover = (segmentId: string | null) => {
    setActiveSegment(segmentId);
    const segment = segmentId ? segments.find((s) => s.id === segmentId) || null : null;
    onSegmentHover?.(segment);
  };

  // SVG dimensions
  const size = 400;
  const center = size / 2;
  const innerRadius = 95;
  const outerRadius = 165;
  const gap = 6;
  const segmentAngle = (360 / segments.length) - gap;

  return (
    <div 
      className="relative w-full max-w-[460px] aspect-square mx-auto lg:mx-0"
      onMouseLeave={() => handleSegmentHover(null)}
    >
      {/* Outer glow ring on hover */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          boxShadow: activeData
            ? `0 0 60px 10px ${activeData.color}20, 0 0 100px 30px ${activeData.color}10`
            : "0 0 0 0 transparent",
        }}
        transition={{ duration: 0.4, ease }}
      />

      {/* Outer subtle ring */}
      <motion.div
        className="absolute inset-2 rounded-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease }}
        style={{
          border: "1px solid hsl(var(--border) / 0.4)",
          background: "radial-gradient(circle at center, transparent 60%, hsl(var(--muted) / 0.15) 100%)",
        }}
      />

      {/* SVG Wheel */}
      <motion.svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${size} ${size}`}
        style={{ 
          transform: "rotate(-90deg)",
        }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease }}
      >
        {/* Glow filter definitions */}
        <defs>
          {segments.map((segment) => (
            <filter key={`glow-${segment.id}`} id={`glow-${segment.id}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>

        {/* Wheel segments */}
        {segments.map((segment, index) => {
          const startAngle = index * (360 / segments.length) + gap / 2;
          const endAngle = startAngle + segmentAngle;
          const isActive = activeSegment === segment.id;
          const hasActive = activeSegment !== null;

          const path = createArcPath(
            center,
            center,
            innerRadius,
            isActive ? outerRadius + 8 : outerRadius,
            startAngle,
            endAngle
          );

          // Label position
          const midAngle = ((startAngle + endAngle) / 2) * (Math.PI / 180);
          const labelRadius = (innerRadius + outerRadius) / 2;
          const labelX = center + labelRadius * Math.cos(midAngle);
          const labelY = center + labelRadius * Math.sin(midAngle);
          const textRotation = (startAngle + endAngle) / 2 + 90;

          return (
            <g key={segment.id}>
              <motion.path
                d={path}
                fill={segment.color}
                stroke="hsl(var(--background))"
                strokeWidth="3"
                style={{ cursor: "pointer" }}
                filter={isActive ? `url(#glow-${segment.id})` : undefined}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: hasActive ? (isActive ? 1 : 0.3) : 0.85,
                  scale: isActive ? 1.02 : 1,
                }}
                transition={{ duration: 0.3, ease }}
                onMouseEnter={() => handleSegmentHover(segment.id)}
              />
              <motion.text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize={isActive ? "14" : "13"}
                fontWeight={isActive ? "600" : "500"}
                letterSpacing="0.02em"
                style={{ pointerEvents: "none" }}
                transform={`rotate(${textRotation}, ${labelX}, ${labelY})`}
                animate={{
                  opacity: hasActive ? (isActive ? 1 : 0.35) : 0.95,
                }}
                transition={{ duration: 0.25 }}
              >
                {segment.label}
              </motion.text>
            </g>
          );
        })}

        {/* Center circle with dynamic border */}
        <motion.circle
          cx={center}
          cy={center}
          r={innerRadius - 6}
          fill="hsl(var(--background))"
          stroke={activeData ? activeData.color : "hsl(var(--border) / 0.5)"}
          strokeWidth={activeData ? "2" : "1"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        />

        {/* Animated center ring */}
        <motion.circle
          cx={center}
          cy={center}
          r={innerRadius - 10}
          fill="none"
          stroke={activeData ? activeData.color : "transparent"}
          strokeWidth="1"
          strokeDasharray="4 4"
          initial={{ opacity: 0 }}
          animate={{
            opacity: activeData ? 0.5 : 0,
            rotate: activeData ? 360 : 0,
          }}
          transition={{
            opacity: { duration: 0.3 },
            rotate: { duration: 20, repeat: Infinity, ease: "linear" },
          }}
          style={{ transformOrigin: "center" }}
        />
      </motion.svg>

      {/* Center content - Enhanced */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[150px] text-center">
          <AnimatePresence mode="wait">
            {activeData ? (
              <motion.div
                key={activeData.id}
                initial={{ opacity: 0, y: 6, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.95 }}
                transition={{ duration: 0.25, ease }}
                className="flex flex-col items-center"
              >
                {/* Icon */}
                <motion.div
                  className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                  style={{ backgroundColor: `${activeData.color}15` }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
                >
                  <activeData.icon className="w-5 h-5" style={{ color: activeData.color }} />
                </motion.div>

                {/* Headline */}
                <h4 className="text-sm font-semibold text-foreground mb-1 leading-tight">
                  {activeData.headline}
                </h4>

                {/* Stat */}
                <motion.div
                  className="flex items-center gap-1.5 mt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  <span className="text-lg font-bold" style={{ color: activeData.color }}>
                    {activeData.stats.value}
                  </span>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    {activeData.stats.label}
                  </span>
                </motion.div>

                {/* Mini CTA */}
                <Link
                  to={activeData.link}
                  className="pointer-events-auto mt-3 flex items-center gap-1 text-[11px] font-medium transition-colors hover:opacity-80"
                  style={{ color: activeData.color }}
                >
                  Learn more
                  <ArrowUpRight className="w-3 h-3" />
                </Link>
              </motion.div>
            ) : (
              <motion.div
                key="default"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Hover to explore<br />the growth system
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Enhanced connector dots with labels */}
      {segments.map((segment, index) => {
        const angle = (index * (360 / segments.length) + (360 / segments.length) / 2 - 90) * (Math.PI / 180);
        const dotRadius = outerRadius + 30;
        const x = center + dotRadius * Math.cos(angle);
        const y = center + dotRadius * Math.sin(angle);
        const isActive = activeSegment === segment.id;

        const xPercent = (x / size) * 100;
        const yPercent = (y / size) * 100;

        return (
          <motion.div
            key={`dot-${segment.id}`}
            className="absolute flex items-center justify-center"
            style={{
              left: `${xPercent}%`,
              top: `${yPercent}%`,
              transform: "translate(-50%, -50%)",
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{ delay: 0.6 + index * 0.05, duration: 0.3, ease }}
          >
            {/* Outer ring on active */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: isActive ? 24 : 8,
                height: isActive ? 24 : 8,
                backgroundColor: isActive ? `${segment.color}20` : "transparent",
                border: isActive ? `1px solid ${segment.color}40` : "none",
              }}
              animate={{
                scale: isActive ? [1, 1.2, 1] : 1,
              }}
              transition={{
                duration: 1.5,
                repeat: isActive ? Infinity : 0,
                ease: "easeInOut",
              }}
            />
            {/* Core dot */}
            <motion.div
              className="rounded-full z-10"
              style={{
                width: isActive ? 10 : 6,
                height: isActive ? 10 : 6,
                backgroundColor: isActive ? segment.color : "hsl(var(--muted-foreground) / 0.3)",
              }}
              transition={{ duration: 0.25 }}
            />
          </motion.div>
        );
      })}

      {/* Connector lines on hover - Enhanced */}
      <AnimatePresence>
        {activeData && (
          <motion.div
            className="absolute pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {segments.map((segment, index) => {
              if (segment.id !== activeSegment) return null;

              const angle = (index * (360 / segments.length) + (360 / segments.length) / 2 - 90) * (Math.PI / 180);
              const startRadius = outerRadius + 25;
              const endRadius = outerRadius + 55;
              
              const x1 = center + startRadius * Math.cos(angle);
              const y1 = center + startRadius * Math.sin(angle);
              const x2 = center + endRadius * Math.cos(angle);
              const y2 = center + endRadius * Math.sin(angle);

              const x1Percent = (x1 / size) * 100;
              const y1Percent = (y1 / size) * 100;
              const x2Percent = (x2 / size) * 100;
              const y2Percent = (y2 / size) * 100;

              const length = Math.sqrt(
                Math.pow(x2Percent - x1Percent, 2) + Math.pow(y2Percent - y1Percent, 2)
              );
              const angleDeg = Math.atan2(y2Percent - y1Percent, x2Percent - x1Percent) * (180 / Math.PI);

              return (
                <motion.div
                  key={`line-${segment.id}`}
                  className="absolute h-[2px] origin-left rounded-full"
                  style={{
                    left: `${x1Percent}%`,
                    top: `${y1Percent}%`,
                    width: `${length}%`,
                    background: `linear-gradient(90deg, ${segment.color}, ${segment.color}50)`,
                    transform: `rotate(${angleDeg}deg)`,
                    boxShadow: `0 0 8px ${segment.color}60`,
                  }}
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  exit={{ scaleX: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease }}
                />
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { segments };
export type { WheelSegment };
export default HeroWheel;